// The plan screen's status of a rebuild that a real profile change requires (P0 2026-10-30). While the stored weeks
// do not reflect the profile yet, the plan is never shown as up to date: updating / waiting for the medical release /
// failed (retry) / needs attention. Nothing is shown when the plan matches the profile.
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowsClockwise, WarningCircle } from '@phosphor-icons/react';
import { getRebuildStatus, runPlanRebuild } from '../../utils/planRebuild';
import { showToast } from '../../utils/toast';

export function rebuildView(st) {
  if (!st) return null;
  if (st.job) return { kind: 'running', text: 'מעדכן את התכנית לפי הפרופיל — השבועות המוצגים יוחלפו בעוד רגע.' };
  if (st.needs_attention) {
    return { kind: 'attention', text: 'עדכון קודם של התכנית לא הושלם ונדרש טיפול. פנה לתמיכה — התכנית לא תשתנה עד אז.' };
  }
  if (!st.required) return null;
  if (st.safety_hold) {
    return { kind: 'hold', text: 'התכנית עדיין לא עודכנה לפי הפרופיל: שינויים בתכנית מושהים עד האישור הרפואי, ואחריו היא תיבנה מחדש.' };
  }
  if (st.last_job && st.last_job.status === 'failed' && st.last_job.rid === st.rid) {
    return { kind: 'failed', text: 'עדכון התכנית נכשל — התכנית המוצגת אינה מעודכנת לשינוי בפרופיל.', retry: 'נסה שוב' };
  }
  return { kind: 'pending', text: 'התכנית המוצגת עדיין לא עודכנה לפי השינוי בפרופיל.', retry: 'עדכן עכשיו' };
}

export default function PlanRebuildStatus({ userId, onUpdated }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!userId) return;
    try { setSt(await getRebuildStatus(userId)); } catch (_e) { /* the status is optional — never blocks the plan */ }
  }, [userId]);
  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener('plan-regen-done', on);
    window.addEventListener('plansUpdated', on);
    return () => { window.removeEventListener('plan-regen-done', on); window.removeEventListener('plansUpdated', on); };
  }, [load]);
  useEffect(() => {                                   // a running rebuild: follow it until it ends
    if (!st || !st.job) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [st, load]);
  const v = rebuildView(st);
  if (!v) return null;
  const retry = async () => {
    setBusy(true);
    const out = await runPlanRebuild(userId).catch(() => ({ ok: false, message: 'עדכון התכנית נכשל — אפשר לנסות שוב.' }));
    setBusy(false);
    if (out.message) showToast(out.message, out.ok ? 'success' : 'error', out.ok ? 5000 : 9000);
    await load();
    if (out.ok && onUpdated) onUpdated();
  };
  const Icon = v.kind === 'running' ? ArrowsClockwise : WarningCircle;
  return (
    <div className="bg-pressed border-y border-border px-4 py-2" role={v.kind === 'running' ? 'status' : 'alert'}
         data-testid="plan-rebuild-status" data-kind={v.kind}>
      <div className="max-w-lg mx-auto flex items-center gap-2 text-xs text-text-body">
        <Icon size={14} weight="bold" className="shrink-0" aria-hidden="true" />
        <span className="flex-1">{v.text}</span>
        {v.retry && (
          <button type="button" onClick={retry} disabled={busy} data-testid="plan-rebuild-retry"
                  className="shrink-0 h-8 px-3 rounded-full border border-border text-text text-xs font-semibold disabled:opacity-60">
            {busy ? 'מעדכן…' : v.retry}
          </button>
        )}
      </div>
    </div>
  );
}
